import { Link } from '@tanstack/react-router';
import {
  type CellContext,
  type ColumnFiltersState,
  createColumnHelper,
  type PaginationState,
  type VisibilityState,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import {
  CalendarClock,
  Loader2,
  Mail,
  MoreHorizontal,
  XCircle,
} from 'lucide-react';
import * as React from 'react';
import { useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { toast } from 'sonner';

import { useCancelTrialBooking } from '~entities/student/api/cancel-trial-booking';
import {
  type TrialStudent,
  useGetAllTrialStudents,
} from '~entities/student/api/get-all-trial-students';
import { useGetTrialLifecycleConflicts } from '~entities/student/api/get-trial-lifecycle-conflicts';
import { useUpdateTrialStudentStatusById } from '~entities/student/api/update-trail-student-status-by-id';
import { useCurrentStudio } from '~entities/studios/model/studio-store';

import { TrialLifecycleConflictCell } from '~features/classes/ui/trial-lifecycle-conflict-cell';
import { TrialRescheduleDialog } from '~features/classes/ui/trial-reschedule-dialog';
import { DateRangePicker } from '~features/payment/ui/date-range-picker';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/shared/shadcn/ui/dialog';
import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import {
  formatTrialDateDisplay,
  formatTrialTimeDisplay,
} from '~shared/helpers/format-trial-datetime';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import { Alert, AlertDescription } from '~shared/shadcn/ui/alert';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import { Card, CardContent } from '~shared/shadcn/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';

const columnHelper = createColumnHelper<TrialStudent>();

function EnrollmentStatusCell({
  info,
}: {
  info: CellContext<
    TrialStudent,
    | 'Trial Scheduled'
    | 'Trial Completed'
    | 'Trial Missed'
    | 'Enrolled'
    | 'Not Enrolled'
    | 'Cancelled'
  >;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const status = info.getValue();
  let badgeVariant: 'default' | 'destructive' | 'secondary' | 'outline' =
    'default';

  const mutation = useUpdateTrialStudentStatusById();

  if (status === 'Trial Completed') {
    badgeVariant = 'default';
  } else if (status === 'Trial Missed' || status === 'Not Enrolled') {
    badgeVariant = 'destructive';
  } else if (status === 'Trial Scheduled') {
    badgeVariant = 'secondary';
  } else if (status === 'Cancelled') {
    badgeVariant = 'outline';
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Badge variant={badgeVariant} className='cursor-pointer'>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Badge>
      </DialogTrigger>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>Update Status</DialogTitle>
          <DialogDescription>
            Are you sure you want to update the status of this student?
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <div className='flex w-full items-center justify-between gap-4'>
            <Button
              className='flex-1'
              onClick={async () => {
                await mutation.mutateAsync({
                  studentId: info.row.original._id,
                  enrollmentStatus: 'Enrolled',
                });
                setIsOpen(false);
              }}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? (
                <Loader2 className='animate-spin' />
              ) : (
                'Mark as Enrolled'
              )}
            </Button>
            <Button
              className='flex-1'
              variant={'destructive'}
              onClick={async () => {
                await mutation.mutateAsync({
                  studentId: info.row.original._id,
                  enrollmentStatus: 'Not Enrolled',
                });
                setIsOpen(false);
              }}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? (
                <Loader2 className='animate-spin' />
              ) : (
                'Mark as Not Enrolled'
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TrialActionsCell({
  info,
  onReschedule,
}: {
  info: CellContext<TrialStudent, unknown>;
  onReschedule: (trial: TrialStudent) => void;
}) {
  const trialClass = info.row.original;
  const cancelMutation = useCancelTrialBooking();
  const isScheduled = trialClass.appointmentStatus === 'Trial Scheduled';

  const handleCancel = async () => {
    try {
      await cancelMutation.mutateAsync({ trialId: trialClass._id });
      toast.success('Trial cancelled.');
    } catch {
      toast.error('We could not cancel this trial. Please try again.');
    }
  };

  return (
    <div className='px-4'>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant='ghost' className='size-8 p-0'>
            <MoreHorizontal className='size-4' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          {isScheduled ? (
            <>
              <DropdownMenuItem
                onClick={() => {
                  onReschedule(trialClass);
                }}
              >
                <CalendarClock className='mr-2 size-4' />
                Reschedule
              </DropdownMenuItem>
              <DropdownMenuItem
                className='text-red-600'
                onClick={() => void handleCancel()}
                disabled={cancelMutation.isPending}
              >
                <XCircle className='mr-2 size-4' />
                {cancelMutation.isPending ? (
                  <Loader2 className='animate-spin' />
                ) : (
                  'Cancel trial'
                )}
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
export const TrialClassesTable = () => {
  const studioTimezone = useCurrentStudio()?.timezone;
  const [rescheduleTrial, setRescheduleTrial] = useState<TrialStudent | null>(
    null,
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(),
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);

  const { data: trailData, isFetching } = useGetAllTrialStudents({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: searchQuery || undefined,
    startDate: dateRange?.from
      ? format(dateRange.from, 'yyyy-MM-dd')
      : undefined,
    endDate: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
  });

  const { data: conflictSummary } = useGetTrialLifecycleConflicts({
    page: 1,
    limit: 1,
  });
  const openConflictCount = conflictSummary?.pagination.total ?? 0;

  const data = trailData?.data ?? [];
  const totalCount = trailData?.pagination.totalCount ?? 0;

  // Define cols first before using it in the table object
  const cols = React.useMemo(
    () => [
      columnHelper.accessor('classId.title', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Class Name'
            disableSorting
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          return <div className='px-4 capitalize'>{value}</div>;
        },
      }),
      columnHelper.accessor('name', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Parent Name'
            disableSorting
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          return <div className='px-4 capitalize'>{value}</div>;
        },
      }),
      columnHelper.accessor('contactNo', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Parent Phone'
            disableSorting
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          return <div className='px-4'>{value}</div>;
        },
      }),
      columnHelper.accessor('email', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Parent Email'
            disableSorting
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className='flex items-center gap-2 px-4'>
              <a
                href={`mailto:${value}`}
                className='text-blue-600 hover:underline'
              >
                {value}
              </a>
              <Mail className='size-4 text-gray-500' />
            </div>
          );
        },
      }),
      columnHelper.accessor('studentName', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Student Name'
            disableSorting
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          return <div className='px-4 capitalize'>{value}</div>;
        },
      }),
      columnHelper.accessor('startTime', {
        id: 'trialDate',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Trial Date'
            disableSorting
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className='px-4'>
              {formatTrialDateDisplay(value, studioTimezone)}
            </div>
          );
        },
      }),
      columnHelper.accessor('startTime', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Start Time'
            disableSorting
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className='px-4'>
              {formatTrialTimeDisplay(value, studioTimezone)}
            </div>
          );
        },
      }),
      columnHelper.accessor('endTime', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='End Time'
            disableSorting
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className='px-4'>
              {formatTrialTimeDisplay(value, studioTimezone)}
            </div>
          );
        },
      }),
      columnHelper.accessor('appointmentStatus', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Status'
            disableSorting
          />
        ),
        cell: (
          info: CellContext<
            TrialStudent,
            | 'Trial Scheduled'
            | 'Trial Completed'
            | 'Trial Missed'
            | 'Enrolled'
            | 'Not Enrolled'
            | 'Cancelled'
          >,
        ) => {
          return <EnrollmentStatusCell info={info} />;
        },
      }),
      columnHelper.display({
        id: 'lifecycleConflict',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Sync'
            disableSorting
          />
        ),
        cell: (info) => (
          <TrialLifecycleConflictCell trial={info.row.original} />
        ),
      }),
      columnHelper.display({
        id: 'action',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Action'
            disableSorting
          />
        ),
        enableHiding: false,
        cell: (info) => {
          return (
            <TrialActionsCell
              info={info}
              onReschedule={(trial) => {
                setRescheduleTrial(trial);
              }}
            />
          );
        },
      }),
    ],
    [studioTimezone],
  );

  return (
    <>
      <Card>
        <CardContent className='pt-6'>
          {openConflictCount > 0 ? (
            <Alert className='mb-4 border-orange-200 bg-orange-50 text-orange-900'>
              <AlertDescription>
                {openConflictCount === 1
                  ? '1 trial needs review because Enrollio and the booking calendar disagree. Look for the Needs review badge in the Sync column.'
                  : `${openConflictCount} trials need review because Enrollio and the booking calendar disagree. Look for the Needs review badge in the Sync column.`}
              </AlertDescription>
            </Alert>
          ) : null}
          <EnrollioTable
            ariaLabel='Trial classes'
            totalLabel='Trial Classes'
            filterHeader={
              <>
                <EnrollioTableSearch
                  placeholder='Search trial classes...'
                  value={searchQuery}
                  onChange={setSearchQuery}
                />
                <DateRangePicker date={dateRange} setDate={setDateRange} />
              </>
            }
            toolbar={(table) => (
              <>
                <Button asChild className='hidden md:inline-flex'>
                  <Link to='/classes'>Back to All Classes</Link>
                </Button>
                <EnrollioTableColumnToggle table={table} />
              </>
            )}
            columnFilters={columnFilters}
            columnVisibility={columnVisibility}
            columns={cols}
            data={data}
            rowCount={totalCount}
            enableRowSelection={true}
            isUpdating={isFetching}
            onColumnFiltersChange={setColumnFilters}
            onColumnVisibilityChange={setColumnVisibility}
            pagination={{
              pageIndex: pagination.pageIndex,
              pageSize: pagination.pageSize,
            }}
            onPaginationChange={setPagination}
            manualPagination
          />
        </CardContent>
      </Card>
      <TrialRescheduleDialog
        trial={rescheduleTrial}
        open={rescheduleTrial != null}
        onOpenChange={(open) => {
          if (!open) {
            setRescheduleTrial(null);
          }
        }}
      />
    </>
  );
};
